
export const ARROW_SPEED_MM_S = 40_000;

export const ARROW_RANGE_MM = 25_000;

export const ARROW_TAIL_S = 0.18;

export const ARROW_HEIGHT_MM = 1_110;

export function looksLikeBow(hands: readonly string[]): boolean {
  return hands.length === 1 && hands[0] === "weaponLeft";
}

export function flightMs(distanceMm: number): number {
  if (!(distanceMm > 0)) return 0;
  return (distanceMm * 1000) / ARROW_SPEED_MM_S;
}

export interface Point {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
}

/** One arrow the client is drawing. */
export interface Flight {
  readonly key: string;
  readonly from: Point;
  readonly to: Point;
  readonly startMs: number;
  readonly landsMs: number;
}

export function arrowAt(flight: Flight, nowMs: number): Point {
  const span = flight.landsMs - flight.startMs;
  const t = span <= 0 ? 1 : (nowMs - flight.startMs) / span;
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  return {
    xMm: flight.from.xMm + (flight.to.xMm - flight.from.xMm) * clamped,
    yMm: flight.from.yMm + (flight.to.yMm - flight.from.yMm) * clamped,
    zMm: flight.from.zMm + (flight.to.zMm - flight.from.zMm) * clamped,
  };
}

/** Where the tail of the ribbon is: `ARROW_TAIL_S` of flight behind the head.
 *
 *  Never behind the launch point, so an arrow that has only just left the bow
 *  has a short streak that grows rather than one that starts underground. */
export function arrowTail(flight: Flight, nowMs: number): Point {
  return arrowAt(flight, nowMs - ARROW_TAIL_S * 1000);
}

export class ArrowFlights {
  private readonly live = new Map<string, Flight>();

  /** Start one. Ignored if `key` is already flying, or if the shot is longer
   *  than an arrow can carry -- which the server would have refused, so seeing
   *  one means the two disagree and drawing it would show a lie. */
  launch(key: string, from: Point, to: Point, nowMs: number): Flight | null {
    if (this.live.has(key)) return null;
    const dx = to.xMm - from.xMm;
    const dy = to.yMm - from.yMm;
    const dz = to.zMm - from.zMm;
    const distanceMm = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (distanceMm > ARROW_RANGE_MM) return null;
    const flight: Flight = {
      key,
      from,
      to,
      startMs: nowMs,
      landsMs: nowMs + flightMs(distanceMm),
    };
    this.live.set(key, flight);
    return flight;
  }

  /** Everything still in the air at `nowMs`, oldest launch first. */
  inFlight(nowMs: number): readonly Flight[] {
    const found: Flight[] = [];
    for (const flight of this.live.values()) {
      if (nowMs < flight.landsMs) found.push(flight);
    }
    return found.sort((a, b) => a.startMs - b.startMs);
  }

  collect(nowMs: number): readonly Flight[] {
    const landed: Flight[] = [];
    for (const flight of this.live.values()) {
      if (nowMs >= flight.landsMs) landed.push(flight);
    }
    for (const flight of landed) this.live.delete(flight.key);
    return landed.sort((a, b) => a.landsMs - b.landsMs);
  }

  /** A new world: nothing that was in the air over the old one is over this
   *  one. The same call `EffectRenderer.clear` exists for. */
  clear(): void {
    this.live.clear();
  }

  get count(): number {
    return this.live.size;
  }
}
